import usePublicCompletion from '@/hooks/usePublicCompletion';
import { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import api from '@/services/api';

/**
 * IntakeOutcomes — patient-completion surface for outcome instruments
 * (NDI, Oswestry, LEFS, DASH, etc.) when sent via a magic-link token
 * with bundle='outcomes' or otherwise containing outcome instrument
 * keys.
 *
 * Renders each instrument as its own card with all questions visible
 * (deliberately not multi-step — patient should see the whole picture
 * to weigh consistency across items). Submits via the public token
 * route which scores server-side and writes patient_self_report rows
 * to outcome_administrations.
 */
export default function IntakeOutcomes({ token, patient, outcomes, lang = 'en', onComplete }) {
  // responses keyed as { [instrumentId]: { [questionId]: numericValue } }
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const path=`${import.meta.env.VITE_API_URL||'/api'}/forms/public/intake/${token}/outcomes/submit`;
  const completion=usePublicCompletion({storageKey:'cervica_intake_outcome_operation_v1_'+token,recoveryPath:path,onRecovered:()=>{setDone(true);onComplete?.()},validateReceipt:receipt=>Array.isArray(receipt.administrations)&&receipt.administrations.length>0&&receipt.administrations.every(row=>row.id&&row.patient_id===patient.id)});
  const [error, setError] = useState(null);

  function setAnswer(instrumentId, questionId, value) {
    setResponses((prev) => ({
      ...prev,
      [instrumentId]: { ...(prev[instrumentId] || {}), [questionId]: value },
    }));
  }

  function isInstrumentComplete(o) {
    const qs = parseQuestions(o);
    const r = responses[o.id] || {};
    return qs.every((q) => r[q.id] !== undefined && r[q.id] !== null);
  }

  function totalAnswered() {
    return outcomes.reduce((sum, o) => {
      const r = responses[o.id] || {};
      return sum + Object.keys(r).length;
    }, 0);
  }

  function totalQuestions() {
    return outcomes.reduce((sum, o) => sum + parseQuestions(o).length, 0);
  }

  async function submit() {
    if(completion.working)return;
    const incomplete = outcomes.filter((o) => !isInstrumentComplete(o));
    if (incomplete.length > 0) {
      setError(`Please answer every question. Missing items in: ${incomplete.map((o) => o.name).join(', ')}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await completion.run(path, {
        submissions: outcomes.map((o) => ({
          instrument_id: o.id,
          instrument_code: o.code,
          responses: responses[o.id] || {},
          patient_id: patient.id,
        })),
      },()=>{setDone(true);onComplete?.()});
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit your answers. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if(completion.pending)return <div className="p-5"><p role="alert">{completion.error||'Your previous responses need confirmation.'}</p><button disabled={completion.working} onClick={completion.retry}>Retry same responses</button></div>;
  if (done) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-center">
        <CheckCircle className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
        <h2 className="text-[20px] font-bold text-gray-900">
          {lang === 'es' ? '¡Gracias!' : 'Thank you!'}
        </h2>
        <p className="mt-2 text-[14px] text-gray-600">
          {lang === 'es'
            ? 'Sus respuestas han sido enviadas al consultorio.'
            : 'Your responses have been sent to the office.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-6">
      <h2 className="text-[18px] font-bold text-gray-900">
        {lang === 'es' ? 'Cuestionarios de salud' : 'Health Questionnaires'}
      </h2>
      <p className="mt-1 text-[12px] text-gray-500">
        {lang === 'es'
          ? 'Estas preguntas ayudan a su quiropráctico a entender cómo le afectan sus síntomas. Responda lo más sinceramente posible.'
          : 'These questions help your chiropractor understand how your symptoms affect you. Please answer as honestly as you can.'}
      </p>
      <p className="mt-2 text-[11px] font-semibold text-teal-700">
        {totalAnswered()} / {totalQuestions()} {lang === 'es' ? 'respondidas' : 'answered'}
      </p>

      <div className="mt-4 space-y-4">
        {outcomes.map((o) => (
          <InstrumentCard
            key={o.id}
            instrument={o}
            responses={responses[o.id] || {}}
            onAnswer={(qId, val) => setAnswer(o.id, qId, val)}
            lang={lang}
          />
        ))}
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {completion.error&&<p role="alert">{completion.error}</p>}
      <button
        type="button"
        onClick={submit}
        disabled={submitting||completion.working}
        className="mt-5 w-full rounded-xl bg-teal-600 px-5 py-3 text-[14px] font-bold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:opacity-50"
      >
        {submitting
          ? (lang === 'es' ? 'Enviando…' : 'Submitting…')
          : (lang === 'es' ? 'Enviar mis respuestas' : 'Submit my answers')}
      </button>
    </div>
  );
}

function parseQuestions(instrument) {
  return typeof instrument.questions === 'string'
    ? JSON.parse(instrument.questions)
    : (instrument.questions || []);
}

function InstrumentCard({ instrument, responses, onAnswer, lang }) {
  const questions = parseQuestions(instrument);

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 sm:p-5">
      <p className="text-[14px] font-bold text-gray-900">{instrument.name}</p>
      {instrument.description && (
        <p className="mt-0.5 text-[12px] text-gray-500">{instrument.description}</p>
      )}

      <div className="mt-3 space-y-3">
        {questions.map((q, idx) => (
          <div key={q.id || idx} className="rounded-lg border border-gray-100 p-3">
            <p className="text-[13px] font-semibold text-gray-800">
              {idx + 1}. {q.text}
            </p>
            <div className="mt-2 space-y-1.5">
              {(q.options || []).map((opt, oi) => {
                const value = opt.value != null ? opt.value : oi;
                const checked = responses[q.id] === value;
                return (
                  <label
                    key={`${q.id}-${oi}`}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 transition-colors ${
                      checked ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`${instrument.id}-${q.id}`}
                      checked={checked}
                      onChange={() => onAnswer(q.id, value)}
                      className="mt-0.5 h-4 w-4 text-teal-600"
                    />
                    <span className="text-[12px] text-gray-700">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
